import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const WEEKDAYS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
];

export const AdminAvailabilityPage = () => {
  const { axiosInstance } = useAuth();
  const [config, setConfig] = useState(null);
  const [newSlot, setNewSlot] = useState('');
  const [newDate, setNewDate] = useState('');

  const fetch = useCallback(async () => {
    const res = await axiosInstance.get('/admin/availability');
    setConfig(res.data);
  }, [axiosInstance]);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async (next) => {
    try {
      await axiosInstance.put('/admin/availability', next);
      setConfig(next);
      toast.success('Disponibilités mises à jour');
    } catch {
      toast.error('Erreur');
    }
  };

  if (!config) return <div className="text-[#6D4C41]/60">Chargement…</div>;

  const addSlot = () => {
    if (!newSlot.trim()) return;
    if (!/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(newSlot.trim())) {
      toast.error('Format attendu : HH:MM-HH:MM');
      return;
    }
    save({ ...config, base_times: [...config.base_times, newSlot.trim()] });
    setNewSlot('');
  };

  const removeSlot = (s) => {
    save({ ...config, base_times: config.base_times.filter((x) => x !== s) });
  };

  const toggleDay = (d) => {
    const closed = config.closed_days.includes(d)
      ? config.closed_days.filter((x) => x !== d)
      : [...config.closed_days, d];
    save({ ...config, closed_days: closed });
  };

  const addBlockedDate = () => {
    if (!newDate) return;
    if (config.blocked_dates.includes(newDate)) {
      toast.info('Date déjà bloquée');
      return;
    }
    save({ ...config, blocked_dates: [...config.blocked_dates, newDate] });
    setNewDate('');
  };

  const removeBlockedDate = (d) => {
    save({ ...config, blocked_dates: config.blocked_dates.filter((x) => x !== d) });
  };

  return (
    <div data-testid="admin-availability" className="max-w-3xl">
      <h1 className="font-heading text-3xl font-bold text-[#3E2723]">Disponibilités de livraison</h1>
      <p className="text-[#6D4C41] mt-1 mb-8">Configurez les créneaux et jours d'ouverture</p>

      {/* Time slots */}
      <section className="bg-white rounded-2xl p-6 border border-[#D4C4AA]/40 mb-6">
        <h2 className="font-heading text-xl font-bold text-[#3E2723] mb-4">Créneaux horaires</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {config.base_times.map((s) => (
            <span key={s} className="inline-flex items-center gap-2 bg-[#F5EFE3] px-3 py-1.5 rounded-full text-sm text-[#3E2723]">
              {s}
              <button onClick={() => removeSlot(s)} className="hover:text-rose-600" data-testid={`remove-slot-${s}`}>
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={newSlot}
            onChange={(e) => setNewSlot(e.target.value)}
            placeholder="ex: 12:00-13:00"
            className="input-field flex-1"
            data-testid="new-slot-input"
          />
          <button onClick={addSlot} className="px-4 py-2 bg-[#D4A373] hover:bg-[#c18e5e] text-white rounded-lg flex items-center gap-2" data-testid="add-slot-btn">
            <Plus size={16} /> Ajouter
          </button>
        </div>
      </section>

      {/* Closed days */}
      <section className="bg-white rounded-2xl p-6 border border-[#D4C4AA]/40 mb-6">
        <h2 className="font-heading text-xl font-bold text-[#3E2723] mb-4">Jours de fermeture hebdomadaire</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {WEEKDAYS.map((d) => {
            const isClosed = config.closed_days.includes(d.value);
            return (
              <button
                key={d.value}
                onClick={() => toggleDay(d.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isClosed ? 'bg-rose-100 text-rose-700 border border-rose-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}
                data-testid={`toggle-day-${d.value}`}
              >
                {d.label}: {isClosed ? 'Fermé' : 'Ouvert'}
              </button>
            );
          })}
        </div>
      </section>

      {/* Blocked dates */}
      <section className="bg-white rounded-2xl p-6 border border-[#D4C4AA]/40 mb-6">
        <h2 className="font-heading text-xl font-bold text-[#3E2723] mb-4">Dates spécifiques bloquées (vacances, jours fériés)</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {config.blocked_dates.map((d) => (
            <span key={d} className="inline-flex items-center gap-2 bg-rose-50 px-3 py-1.5 rounded-full text-sm text-rose-700">
              {d}
              <button onClick={() => removeBlockedDate(d)} className="hover:text-rose-900">
                <X size={14} />
              </button>
            </span>
          ))}
          {config.blocked_dates.length === 0 && <span className="text-sm text-[#6D4C41]/60">Aucune date bloquée</span>}
        </div>
        <div className="flex gap-2">
          <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="input-field flex-1" data-testid="new-blocked-date" />
          <button onClick={addBlockedDate} className="px-4 py-2 bg-[#D4A373] hover:bg-[#c18e5e] text-white rounded-lg flex items-center gap-2">
            <Plus size={16} /> Bloquer
          </button>
        </div>
      </section>

      {/* Advance days */}
      <section className="bg-white rounded-2xl p-6 border border-[#D4C4AA]/40">
        <h2 className="font-heading text-xl font-bold text-[#3E2723] mb-2">Fenêtre de réservation</h2>
        <p className="text-sm text-[#6D4C41] mb-3">Nombre de jours à l'avance que le client peut réserver</p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="1"
            max="60"
            value={config.advance_days}
            onChange={(e) => setConfig({ ...config, advance_days: parseInt(e.target.value) || 1 })}
            className="input-field w-24"
            data-testid="advance-days-input"
          />
          <span className="text-[#6D4C41]">jours</span>
          <button onClick={() => save(config)} className="ml-auto px-4 py-2 bg-[#3E2723] hover:bg-[#5d3b34] text-white rounded-lg" data-testid="save-advance-days">
            Enregistrer
          </button>
        </div>
      </section>
    </div>
  );
};
